
import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Search, Plus, Package, Trash2, Edit, RotateCcw, Loader2, Smartphone } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAppCatalogSearch } from "@/hooks/useAppCatalogSearch";
import { useMutation, useQuery, useConvex } from "convex/react";
import type { InstalledAppMeta } from "./InstalledAppsReviewModal";
import { api } from "../../api";
import { AppIcon } from "./AppIcon";
import { InstalledAppsReviewModal } from "./InstalledAppsReviewModal";

interface DeviceAppsWhitelistContentProps {
  deviceId: string;
}

export const DeviceAppsWhitelistContent = ({ deviceId }: DeviceAppsWhitelistContentProps) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [showAddAppSearch, setShowAddAppSearch] = useState(false);
  const [removedLevelApps, setRemovedLevelApps] = useState<any[]>([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const { toast } = useToast();
  const convex = useConvex();

  // Get device details to get the deviceConfig
  const deviceData = useQuery(api.devices.get.details, 
    deviceId ? { device: deviceId as any } : "skip"
  );

  // Get current device app configuration
  const deviceAppConfig = useQuery(api.devices.apps.getConfig,
    deviceData?.device?.deviceConfig ? { deviceConfig: deviceData.device.deviceConfig } : "skip"
  );

  // Get all device-specific whitelisted app IDs
  const deviceWhitelistedAppIds = Object.keys(deviceAppConfig?.allowedApps || {})
    .filter(appId => deviceAppConfig?.allowedApps[appId] === 'device');

  // Get all level-specific whitelisted app IDs
  const levelWhitelistedAppIds = Object.keys(deviceAppConfig?.allowedApps || {})
    .filter(appId => deviceAppConfig?.allowedApps[appId] === 'level');

  // Combine all app IDs for fetching
  const allWhitelistedAppIds = [...deviceWhitelistedAppIds, ...levelWhitelistedAppIds];

  // Fetch specific whitelisted apps by their IDs directly
  const whitelistedAppsQuery = useQuery(api.apps.get.apps,
    allWhitelistedAppIds.length > 0 
      ? { apps: allWhitelistedAppIds as any[] } 
      : "skip"
  );

  // Map the response to include full app data with IDs
  const whitelistedApps = useMemo(() => {
    if (!whitelistedAppsQuery) return undefined;
    
    return allWhitelistedAppIds.map((appId, index) => {
      const appData = whitelistedAppsQuery[index];
      return {
        id: appId,
        name: appData?.name || 'Unknown App',
        bundleId: appData?.bundleId || appId,
        iconUrl: appData?.iconUrl,
      };
    }).filter(app => app.name !== 'Unknown App' || app.bundleId !== app.id);
  }, [whitelistedAppsQuery, allWhitelistedAppIds]);

  // Use the app catalog search hook
  const { data: searchResults, isLoading: isSearching } = useAppCatalogSearch(searchQuery);
  
  // Mutation for updating device apps
  const updateDeviceApps = useMutation(api.devices.apps.update);

  // Helper to get app source
  const getAppSource = (appId: string) => {
    if (deviceWhitelistedAppIds.includes(appId)) return 'Device';
    if (levelWhitelistedAppIds.includes(appId)) return 'Level';
    return 'Unknown';
  };

  // Helper to check if an app was removed from level
  const isRemovedLevelApp = (appId: string) => {
    return removedLevelApps.some(app => app.id === appId);
  };

  const handleAddToWhitelist = async (app: any) => {
    if (!deviceData?.device?.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Get current device allowed apps and add the new one
      const currentAllowedApps = [...deviceWhitelistedAppIds, app.id];
      
      await updateDeviceApps({
        device: deviceId as any,
        allowedApps: currentAllowedApps as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {})
          .filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[],
        appFilter: Object.fromEntries(
          Object.entries(deviceAppConfig?.appFilter || {})
            .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
            .map(([key, config]) => [key, (config as any).tagFilter])
        )
      });
      
      toast({
        title: "App Added",
        description: `${app.appName} has been added to the device whitelist.`,
      });
      
      setSearchQuery("");
      setShowAddAppSearch(false);
    } catch (error) {
      console.error('Error adding app to device whitelist:', error);
      toast({
        title: "Error",
        description: `Failed to add app to device whitelist: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  // Handle importing selected apps from the installed apps modal.
  // Apps not in the catalog are automatically added before whitelisting.
  const handleImportFromDevice = async (selectedApps: InstalledAppMeta[]) => {
    if (!deviceData?.device?.deviceConfig || selectedApps.length === 0) return;

    try {
      // Filter out apps already whitelisted
      const alreadyWhitelistedBundleIds = new Set(
        (whitelistedApps || []).map((app: any) => app.bundleId)
      );
      const newApps = selectedApps.filter(a => !alreadyWhitelistedBundleIds.has(a.bundleId));

      if (newApps.length === 0) {
        toast({ title: "No new apps", description: "All selected apps are already whitelisted." });
        return;
      }

      // Resolve bundle IDs → internal Convex catalog IDs (skip apps not in catalog)
      const resolvedIds: string[] = [];
      const skippedApps: string[] = [];
      for (const app of newApps) {
        const result = await convex.query(api.apps.list.all, {
          query: app.bundleId,
          paginationOpts: { numItems: 10, cursor: null },
        });
        const match = result.page.find((a: any) => a.bundleId === app.bundleId);

        if (match) {
          resolvedIds.push(match.id);
        } else {
          // App not in catalog — skip it, don't try to add
          skippedApps.push(app.name || app.bundleId);
        }
      }

      if (resolvedIds.length === 0) {
        toast({ title: "No apps added", description: "Failed to resolve any selected apps." });
        setShowImportModal(false);
        return;
      }

      const updatedAllowedApps = [...deviceWhitelistedAppIds, ...resolvedIds];

      await updateDeviceApps({
        device: deviceId as any,
        allowedApps: updatedAllowedApps as any[],
        blockedApps: Object.keys(deviceAppConfig?.blockedApps || {})
          .filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[],
        appFilter: Object.fromEntries(
          Object.entries(deviceAppConfig?.appFilter || {})
            .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
            .map(([key, config]) => [key, (config as any).tagFilter])
        )
      });

      toast({
        title: "Apps Imported",
        description: `${resolvedIds.length} app${resolvedIds.length !== 1 ? 's' : ''} added to whitelist.`,
      });

      if (skippedApps.length > 0) {
        toast({
          title: "Apps Not in Catalog",
          description: `${skippedApps.length} app${skippedApps.length !== 1 ? 's are' : ' is'} not in the app catalog. Please add them at a later point: ${skippedApps.join(', ')}`,
        });
      }

      setShowImportModal(false);
    } catch (error) {
      console.error('Error importing apps to whitelist:', error);
      toast({
        title: "Error",
        description: `Failed to import apps: ${(error as any)?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleRemoveApp = async (appId: string, source: string) => {
    if (!deviceData?.device?.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found.",
        variant: "destructive",
      });
      return;
    }

    const app = whitelistedApps?.find((a: any) => a.id === appId);

    try {
      if (source === 'Device') {
        // Remove from device whitelist
        const updatedDeviceApps = deviceWhitelistedAppIds.filter(id => id !== appId);
        
        await updateDeviceApps({
          device: deviceId as any,
          allowedApps: updatedDeviceApps as any[],
          blockedApps: Object.keys(deviceAppConfig?.blockedApps || {})
            .filter(appId => deviceAppConfig?.blockedApps[appId] === 'device') as any[],
          appFilter: Object.fromEntries(
            Object.entries(deviceAppConfig?.appFilter || {})
              .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
              .map(([key, config]) => [key, (config as any).tagFilter])
          )
        });
      } else if (source === 'Level' && app) {
        // Add to removed level apps list for the second table
        setRemovedLevelApps(prev => [...prev, app]);
        
        // Block this app at device level (add to device blocked apps)
        const currentBlockedApps = Object.keys(deviceAppConfig?.blockedApps || {})
          .filter(appId => deviceAppConfig?.blockedApps[appId] === 'device');
        
        await updateDeviceApps({
          device: deviceId as any,
          allowedApps: deviceWhitelistedAppIds as any[],
          blockedApps: [...currentBlockedApps, appId] as any[],
          appFilter: Object.fromEntries(
            Object.entries(deviceAppConfig?.appFilter || {})
              .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
              .map(([key, config]) => [key, (config as any).tagFilter])
          )
        });
      }
      
      toast({
        title: "App Removed",
        description: `${app?.name} has been removed from the whitelist.`,
      });
    } catch (error) {
      console.error('Error removing app:', error);
      toast({
        title: "Error",
        description: `Failed to remove app: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const handleRestoreApp = async (appId: string) => {
    if (!deviceData?.device?.deviceConfig) {
      toast({
        title: "Error",
        description: "Device configuration not found.",
        variant: "destructive",
      });
      return;
    }

    try {
      // Remove from blocked apps to restore level access
      const currentBlockedApps = Object.keys(deviceAppConfig?.blockedApps || {})
        .filter(id => deviceAppConfig?.blockedApps[id] === 'device' && id !== appId);
      
      await updateDeviceApps({
        device: deviceId as any,
        allowedApps: deviceWhitelistedAppIds as any[],
        blockedApps: currentBlockedApps as any[],
        appFilter: Object.fromEntries(
          Object.entries(deviceAppConfig?.appFilter || {})
            .filter(([_, config]) => config && typeof config === 'object' && 'tagFilter' in config)
            .map(([key, config]) => [key, (config as any).tagFilter])
        )
      });
      
      // Remove from removed apps list
      setRemovedLevelApps(prev => prev.filter(app => app.id !== appId));
      
      const app = removedLevelApps.find(a => a.id === appId);
      toast({
        title: "App Restored",
        description: `${app?.name} has been restored to the whitelist.`,
      });
    } catch (error) {
      console.error('Error restoring app:', error);
      toast({
        title: "Error",
        description: `Failed to restore app: ${error?.message || 'Unknown error'}`,
        variant: "destructive",
      });
    }
  };

  const isLoading = deviceData === undefined || deviceAppConfig === undefined || (allWhitelistedAppIds.length > 0 && whitelistedAppsQuery === undefined);

  // Create combined list of apps with source information
  const combinedApps = whitelistedApps
    ? whitelistedApps
        .filter((app: any) => !isRemovedLevelApp(app.id))
        .map((app: any) => ({
          ...app,
          source: getAppSource(app.id)
        }))
    : [];

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">App Whitelist</h3>
          <p className="text-sm text-gray-600">
            Apps allowed for this device from both device and level settings
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowImportModal(true)}
          >
            <Smartphone className="w-4 h-4 mr-1" />
            Import from Device
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowAddAppSearch(!showAddAppSearch)}
          >
            <Plus className="w-4 h-4 mr-1" />
            Add App
          </Button>
          {isEditMode ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(false)}
            >
              Done
            </Button>
          ) : (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditMode(true)}
            >
              <Edit className="w-4 h-4 mr-1" />
              Edit
            </Button>
          )}
        </div>
      </div>

      {/* Add App Search */}
      {showAddAppSearch && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Add Apps to Whitelist</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search for apps to add..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {searchQuery && (
              <div className="space-y-2">
                {isSearching ? (
                  <div className="text-center py-4">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                  </div>
                ) : searchResults && searchResults.length > 0 ? (
                  <>
                    <h4 className="font-medium text-sm">Search Results</h4>
                    {searchResults
                      .filter(app => !allWhitelistedAppIds.includes(app.id))
                      .map((app) => (
                      <div key={app.bundleId} className="flex items-center justify-between p-3 border rounded-lg">
                        <div className="flex items-center gap-3">
                          <AppIcon app_icon_url={app.iconUrl} app_name={app.appName} />
                          <div>
                            <p className="font-medium text-sm">{app.appName}</p>
                            <p className="text-xs text-gray-500">{app.bundleId}</p>
                          </div>
                        </div>
                        <Button size="sm" onClick={() => handleAddToWhitelist(app)}>
                          <Plus className="w-4 h-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    ))}
                    {searchResults.filter(app => !allWhitelistedAppIds.includes(app.id)).length === 0 && (
                      <p className="text-sm text-gray-500">All search results are already whitelisted.</p>
                    )}
                  </>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500">No apps found for "{searchQuery}"</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Main Whitelist Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin mr-2" />
              <span>Loading app whitelist...</span>
            </div>
          ) : combinedApps.length === 0 ? (
            <div className="text-center py-8">
              <Package className="w-12 h-12 text-gray-400 mb-4 mx-auto" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Apps Whitelisted</h3>
              <p className="text-gray-600 text-center max-w-md mx-auto">
                No apps are currently whitelisted for this device. Click "Add App" to search and add apps.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Source</TableHead>
                  {isEditMode && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {combinedApps.map((app: any) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <AppIcon app_icon_url={app.iconUrl} app_name={app.name} />
                        <div>
                          <p className="font-medium text-sm">{app.name}</p>
                          <p className="text-xs text-gray-500">{app.bundleId}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={app.source === 'Device' ? 'default' : 'secondary'}>
                        {app.source}
                      </Badge>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRemoveApp(app.id, app.source)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Removed Level Apps Table */}
      {removedLevelApps.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Apps that were on the whitelist by level</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App</TableHead>
                  <TableHead>Source</TableHead>
                  {isEditMode && <TableHead className="w-[100px]">Actions</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {removedLevelApps.map((app: any) => (
                  <TableRow key={app.id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <AppIcon app_icon_url={app.iconUrl} app_name={app.name} />
                        <div>
                          <p className="font-medium text-sm">{app.name}</p>
                          <p className="text-xs text-gray-500">{app.bundleId}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary">Level</Badge>
                    </TableCell>
                    {isEditMode && (
                      <TableCell>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRestoreApp(app.id)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <RotateCcw className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <InstalledAppsReviewModal
        open={showImportModal}
        onOpenChange={setShowImportModal}
        deviceId={deviceId}
        pendingMode="whitelist"
        mode="addToWhitelist"
        onConfirm={(selectedApps) => handleImportFromDevice(selectedApps)}
        onCancel={() => setShowImportModal(false)}
      />
    </div>
  );
};

